import {
  HStack,
  VStack,
} from "@chakra-ui/react";
import { Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import useThemeColors from "../../../hooks/useThemeColors";
import NavMenu from "../../CustomUi/NavMenu";
import TopBar from "../../CustomUi/TopBar";
import { useBreakPoint } from "../../../hooks/useBreakPoint";

function Layout() {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [savedIsCollapsed, setSavedIsCollapsed] = useState(false);
  const colors = useThemeColors();
  const { isMobile, isTablet } = useBreakPoint();
  // Load initial collapse state from localStorage
  useEffect(() => {
    const savedCollapseState = localStorage.getItem("navCollapsed");
    const initialCollapsed = savedCollapseState
      ? JSON.parse(savedCollapseState)
      : false;
    setSavedIsCollapsed(initialCollapsed);
    // On tablet/mobile, always collapse regardless of saved preference
    if (isTablet) {
      setIsCollapsed(true);
    } else {
      // On desktop, use saved preference or default to expanded
      setIsCollapsed(savedIsCollapsed);
    }
  }, []);

  // Handle responsive behavior
  useEffect(() => {
    if (isTablet) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(savedIsCollapsed);
    }
  }, [isTablet]);

  const handleToggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    setSavedIsCollapsed(newCollapsedState);

    // Save to localStorage only when user manually toggles (not on responsive changes)
    if (!isTablet) {
      localStorage.setItem("navCollapsed", JSON.stringify(newCollapsedState));
    }
  };

  const getPageName = (pathname: string): string => {
    switch (pathname) {
      case "/":
        return "Dashboard";
      case "/students":
        return "Students";
      case "/results":
        return "Results";
      case "/admin/test":
        return "Admin Test";
      case "/admin/school":
        return "School Settings";
      case "/admin/academic/sessions":
        return "Sessions";
      case "/admin/academic/terms":
        return "Terms";
      case "/admin/academic/levels":
        return "Levels";
      case "/admin/academic/classes":
        return "Classes";
      case "/admin/academic/subjects":
        return "Subjects";
      case "/admin/academic/assessment-types":
        return "Assessment Types";
      case "/admin/academic/grades":
        return "Grades";
      case "/admin/users":
        return "User Management";
      case "/admin/audit-trail":
        return "Audit Trail";
      case "/admin/notifications":
        return "Notifications";
      case "/admin/data/export":
        return "Export Data";
      case "/admin/data/delete":
        return "Delete Data";
      case "/admin/data/bulk-reports":
        return "Bulk Reports";
      case "/admin/promotion":
        return "Promotions";
      case "/admin/promotion/eligibility":
        return "Promotion Eligibility";
      default:
        return "";
    }
  };

  const pageName = getPageName(location.pathname);

  return (
    <HStack direction="row" h="100vh" overflow="hidden" align={"stretch"} gap={0}>
      {/* Sidebar Navigation - Always visible on desktop, collapsible */}
      {!isMobile && (
        <NavMenu
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          onToggleCollapse={handleToggleCollapse}
          pageName={pageName}
        />
      )}

      {/* Main Content Area */}
      <VStack
        flex={1}
        gap={0}
        direction="column"
        overflow="hidden"
        align={"stretch"}
      >
        {/* Compact Top Bar - Only essential elements */}
        <TopBar
          pageName={pageName}
          isCollapsed={isCollapsed}
          isMobile={isMobile || false}
        />

        {/* Main Content with improved spacing */}
        <VStack
          p={[2, 4]}
          as="main"
          align={"stretch"}
          overflow="auto"
          h={"full"}
          bg={colors.background.secondary}
        >
          <Outlet />
        </VStack>
      </VStack>
    </HStack>
  );
}

export default Layout;
